import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import React from 'react'
import { Hugeicon } from '../../../icons/Hugeicon'

const BusinessCalendarLanding: React.FC = () => {
  return (
    <LandingContainer>
      <HeroSection>
        <HeroContent>
          <Eyebrow>Business Calendar</Eyebrow>
          <HeroTitle>
            Your events.
            <br />
            <TitleAccent>More places.</TitleAccent>
          </HeroTitle>
          <HeroDescription>
            Embed a beautiful calendar on your website. When you add events, they also get distributed across our
            network of community sites in your area — so you reach more people without extra work.
          </HeroDescription>
          <HeroCTA href="/dash/calendar-for-businesses/signup">
            Start Free Trial
            <ArrowIcon />
          </HeroCTA>
          <TrialNote>14-day free trial. Plans from $25/month. Cancel anytime.</TrialNote>
        </HeroContent>

        <HeroVisual>
          <NetworkDiagram>
            <CentralNode>
              <CalendarIcon />
              <NodeLabel>Your Calendar</NodeLabel>
            </CentralNode>

            <ConnectionLine style={{ transform: 'rotate(-60deg)' }} />
            <ConnectionLine style={{ transform: 'rotate(-20deg)' }} />
            <ConnectionLine style={{ transform: 'rotate(20deg)' }} />
            <ConnectionLine style={{ transform: 'rotate(60deg)' }} />

            <SatelliteNode style={{ top: '-10%', left: '50%' }}>
              <SatelliteIcon>
                <GlobeSmall />
              </SatelliteIcon>
              <SatelliteLabel>Town Website</SatelliteLabel>
            </SatelliteNode>

            <SatelliteNode style={{ top: '20%', right: '-5%' }}>
              <SatelliteIcon>
                <GlobeSmall />
              </SatelliteIcon>
              <SatelliteLabel>Local News</SatelliteLabel>
            </SatelliteNode>

            <SatelliteNode style={{ bottom: '20%', right: '-5%' }}>
              <SatelliteIcon>
                <GlobeSmall />
              </SatelliteIcon>
              <SatelliteLabel>Chamber Site</SatelliteLabel>
            </SatelliteNode>

            <SatelliteNode style={{ bottom: '-10%', left: '50%' }}>
              <SatelliteIcon>
                <GlobeSmall />
              </SatelliteIcon>
              <SatelliteLabel>Library</SatelliteLabel>
            </SatelliteNode>

            <SatelliteNode style={{ bottom: '20%', left: '-5%' }}>
              <SatelliteIcon>
                <GlobeSmall />
              </SatelliteIcon>
              <SatelliteLabel>Parks & Rec</SatelliteLabel>
            </SatelliteNode>

            <SatelliteNode style={{ top: '20%', left: '-5%' }}>
              <SatelliteIcon>
                <GlobeSmall />
              </SatelliteIcon>
              <SatelliteLabel>Your Website</SatelliteLabel>
            </SatelliteNode>

            <PulseRing />
            <PulseRing style={{ animationDelay: '1s' }} />
            <PulseRing style={{ animationDelay: '2s' }} />
          </NetworkDiagram>
        </HeroVisual>
      </HeroSection>

      <FeaturesSection>
        <FeaturesSectionTitle>One calendar. More reach.</FeaturesSectionTitle>

        <FeaturesGrid>
          <FeatureCard>
            <FeatureIconWrapper color="#3b82f6">
              <EmbedIcon />
            </FeatureIconWrapper>
            <FeatureTitle>Embed on Your Site</FeatureTitle>
            <FeatureDescription>
              Add a branded calendar to your website with a simple code snippet. Matches your colors and style
              automatically.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard highlight>
            <FeatureIconWrapper color="#22c55e">
              <NetworkIcon />
            </FeatureIconWrapper>
            <FeatureTitle>Network Distribution</FeatureTitle>
            <FeatureDescription>
              Every event you post appears on community websites across your area. One submission, many
              placements.
            </FeatureDescription>
            <FeatureBadge>Key Advantage</FeatureBadge>
          </FeatureCard>

          <FeatureCard>
            <FeatureIconWrapper color="#f59e0b">
              <ChartIcon />
            </FeatureIconWrapper>
            <FeatureTitle>Track Performance</FeatureTitle>
            <FeatureDescription>
              See how many people view your events across all sites. Understand which events drive the most engagement.
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>
      </FeaturesSection>

      <HowItWorksSection>
        <HowItWorksTitle>How it works</HowItWorksTitle>

        <StepsContainer>
          <Step>
            <StepNumber>1</StepNumber>
            <StepContent>
              <StepTitle>Set up your calendar</StepTitle>
              <StepDescription>Choose your organization and customize your calendar&apos;s appearance.</StepDescription>
            </StepContent>
          </Step>

          <StepConnector />

          <Step>
            <StepNumber>2</StepNumber>
            <StepContent>
              <StepTitle>Embed on your website</StepTitle>
              <StepDescription>Copy a simple code snippet and paste it anywhere on your site.</StepDescription>
            </StepContent>
          </Step>

          <StepConnector />

          <Step>
            <StepNumber>3</StepNumber>
            <StepContent>
              <StepTitle>Add events once</StepTitle>
              <StepDescription>
                Submit your events once. They appear on your site and get distributed to community sites in your area.
              </StepDescription>
            </StepContent>
          </Step>
        </StepsContainer>
      </HowItWorksSection>

      <ComparisonSection>
        <ComparisonTitle>Why businesses choose CivicLift</ComparisonTitle>

        <ComparisonTable>
          <ComparisonHeader>
            <ComparisonHeaderCell />
            <ComparisonHeaderCell highlight>CivicLift</ComparisonHeaderCell>
            <ComparisonHeaderCell>Other Calendars</ComparisonHeaderCell>
          </ComparisonHeader>

          <ComparisonRow>
            <ComparisonLabel>Embed on your website</ComparisonLabel>
            <ComparisonValue highlight>
              <CheckIcon />
            </ComparisonValue>
            <ComparisonValue>
              <CheckIcon muted />
            </ComparisonValue>
          </ComparisonRow>

          <ComparisonRow>
            <ComparisonLabel>Auto-distribute to community sites</ComparisonLabel>
            <ComparisonValue highlight>
              <CheckIcon />
            </ComparisonValue>
            <ComparisonValue>
              <XIcon />
            </ComparisonValue>
          </ComparisonRow>

          <ComparisonRow>
            <ComparisonLabel>Reach local audience network</ComparisonLabel>
            <ComparisonValue highlight>
              <CheckIcon />
            </ComparisonValue>
            <ComparisonValue>
              <XIcon />
            </ComparisonValue>
          </ComparisonRow>

          <ComparisonRow>
            <ComparisonLabel>Brand customization</ComparisonLabel>
            <ComparisonValue highlight>
              <CheckIcon />
            </ComparisonValue>
            <ComparisonValue>
              <CheckIcon muted />
            </ComparisonValue>
          </ComparisonRow>

          <ComparisonRow>
            <ComparisonLabel>Cross-site analytics</ComparisonLabel>
            <ComparisonValue highlight>
              <CheckIcon />
            </ComparisonValue>
            <ComparisonValue>
              <XIcon />
            </ComparisonValue>
          </ComparisonRow>
        </ComparisonTable>
      </ComparisonSection>

      <CTASection>
        <CTAContent>
          <CTATitle>Ready to reach more people?</CTATitle>
          <CTADescription>Start your 14-day free trial. Plans from $25/month or $250/year.</CTADescription>
          <CTAButton href="/dash/calendar-for-businesses/signup">
            Get Started Free
            <ArrowIcon />
          </CTAButton>
        </CTAContent>
      </CTASection>
    </LandingContainer>
  )
}

export default BusinessCalendarLanding

// Icons
const ArrowIcon = () => (
  <Hugeicon name="arrow-right-long" size={20} />
)

const CalendarIcon = () => (
  <Hugeicon name="calendar" size={32} />
)

const GlobeSmall = () => (
  <Hugeicon name="globe" size={16} />
)

const EmbedIcon = () => (
  <Hugeicon name="source-code" size={28} />
)

const NetworkIcon = () => (
  <Hugeicon name="hierarchy" size={28} />
)

const ChartIcon = () => (
  <Hugeicon name="chart-column" size={28} />
)

const CheckIcon: React.FC<{ muted?: boolean }> = ({ muted }) => (
  <Hugeicon name="tick" size={20} style={{ opacity: muted ? 0.4 : 1 }} />
)

const XIcon = () => (
  <Hugeicon name="cancel" size={20} style={{ opacity: 0.3 }} />
)

// Animations
const pulse = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.6;
  }
  100% {
    transform: translate(-50%, -50%) scale(2.5);
    opacity: 0;
  }
`

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
`

// Styled Components
const LandingContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  min-height: 100vh;
  min-height: calc(100vh - var(--impersonation-banner-height, 0px));
  background: white;
`

const HeroSection = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  padding: 48px 48px 80px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 32px 24px 48px;
    gap: 48px;
  }
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
`

const Eyebrow = styled.span`
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #3b82f6;
  margin-bottom: 16px;
`

const HeroTitle = styled.h1`
  font-family: 'Georgia', serif;
  font-size: 48px;
  font-weight: 400;
  line-height: 1.15;
  color: #0f172a;
  margin: 0 0 24px;

  @media (max-width: 900px) {
    font-size: 36px;
  }
`

const TitleAccent = styled.span`
  color: #3b82f6;
  font-style: italic;
`

const HeroDescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: #475569;
  margin: 0 0 32px;
  max-width: 480px;
`

const HeroCTA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #0f172a;
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 16px 28px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: #1e293b;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.2);
    color: white;
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: translateX(4px);
  }
`

const TrialNote = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 16px 0 0;
`

const HeroVisual = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  @media (max-width: 900px) {
    display: none;
  }
`

const NetworkDiagram = styled.div`
  position: relative;
  width: 320px;
  height: 320px;
`

const CentralNode = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: white;
  z-index: 10;
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.3);
  animation: ${float} 4s ease-in-out infinite;
`

const NodeLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
`

const ConnectionLine = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100px;
  height: 2px;
  background: linear-gradient(90deg, #cbd5e1 0%, transparent 100%);
  transform-origin: left center;
`

const SatelliteNode = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  transform: translate(-50%, -50%);
`

const SatelliteIcon = styled.div`
  width: 40px;
  height: 40px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    transform: scale(1.1);
  }
`

const SatelliteLabel = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: #64748b;
  white-space: nowrap;
`

const PulseRing = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100px;
  height: 100px;
  border: 2px solid #3b82f6;
  border-radius: 20px;
  animation: ${pulse} 3s ease-out infinite;
`

const FeaturesSection = styled.section`
  padding: 80px 48px;
  background: white;

  @media (max-width: 900px) {
    padding: 48px 24px;
  }
`

const FeaturesSectionTitle = styled.h2`
  font-family: 'Georgia', serif;
  font-size: 32px;
  font-weight: 400;
  color: #0f172a;
  text-align: center;
  margin: 0 0 48px;
`

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`

const FeatureCard = styled.div<{ highlight?: boolean }>`
  position: relative;
  background: ${({ highlight }) => (highlight ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' : '#f8fafc')};
  border: 2px solid ${({ highlight }) => (highlight ? '#86efac' : 'transparent')};
  border-radius: 16px;
  padding: 32px 24px;
  text-align: center;
`

const FeatureIconWrapper = styled.div<{ color: string }>`
  width: 56px;
  height: 56px;
  background: ${({ color }) => color}15;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: ${({ color }) => color};
`

const FeatureTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 12px;
`

const FeatureDescription = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
`

const FeatureBadge = styled.span`
  position: absolute;
  top: -10px;
  right: 20px;
  background: #22c55e;
  color: white;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 4px;
`

const HowItWorksSection = styled.section`
  padding: 80px 48px;
  background: #f8fafc;

  @media (max-width: 900px) {
    padding: 48px 24px;
  }
`

const HowItWorksTitle = styled.h2`
  font-family: 'Georgia', serif;
  font-size: 32px;
  font-weight: 400;
  color: #0f172a;
  text-align: center;
  margin: 0 0 48px;
`

const StepsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 24px;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 32px;
  }
`

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1;
  max-width: 240px;
`

const StepNumber = styled.div`
  width: 48px;
  height: 48px;
  background: #0f172a;
  color: white;
  font-size: 20px;
  font-weight: 700;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`

const StepContent = styled.div``

const StepTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 8px;
`

const StepDescription = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
`

const StepConnector = styled.div`
  width: 60px;
  height: 2px;
  background: #cbd5e1;
  margin-top: 24px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    width: 2px;
    height: 40px;
    margin: 0;
  }
`

const ComparisonSection = styled.section`
  padding: 80px 48px;
  background: white;

  @media (max-width: 900px) {
    padding: 48px 24px;
  }
`

const ComparisonTitle = styled.h2`
  font-family: 'Georgia', serif;
  font-size: 32px;
  font-weight: 400;
  color: #0f172a;
  text-align: center;
  margin: 0 0 48px;
`

const ComparisonTable = styled.div`
  max-width: 600px;
  margin: 0 auto;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
`

const ComparisonHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
`

const ComparisonHeaderCell = styled.div<{ highlight?: boolean }>`
  padding: 16px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ highlight }) => (highlight ? '#0f172a' : '#64748b')};
  text-align: center;

  &:first-of-type {
    text-align: left;
  }
`

const ComparisonRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
`

const ComparisonLabel = styled.div`
  padding: 16px;
  font-size: 14px;
  color: #475569;
`

const ComparisonValue = styled.div<{ highlight?: boolean }>`
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ highlight }) => (highlight ? '#f0fdf4' : 'transparent')};
  color: ${({ highlight }) => (highlight ? '#22c55e' : '#94a3b8')};
`

const CTASection = styled.section`
  padding: 80px 48px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);

  @media (max-width: 900px) {
    padding: 48px 24px;
  }
`

const CTAContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
`

const CTATitle = styled.h2`
  font-family: 'Georgia', serif;
  font-size: 36px;
  font-weight: 400;
  color: white;
  margin: 0 0 16px;
`

const CTADescription = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: #94a3b8;
  margin: 0 0 32px;
`

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: white;
  color: #0f172a;
  font-size: 16px;
  font-weight: 600;
  padding: 16px 28px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    color: #0f172a;
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: translateX(4px);
  }
`
